import { api } from '../../core/network/api';
import type { CharacterClassId } from '../config/character-roster';
import type { ItemCopy } from '../config/item-copy';

/** design/gdd/cosmetics-store.md REST surface — plain wrappers, same convention as contracts-api.ts. */

export interface CosmeticDefRow {
  id: string;
  /**
   * The shelf name. Optional so an older server still parses — a build that
   * predates the named catalogue sends none, and `cosmeticLabel` falls back to
   * prettifying the id the way the shop did before 2026-09-04.
   */
  name?: string;
  /** 'SKIN' or 'EMOTE'. Optional so an older server still parses. */
  kind?: 'SKIN' | 'EMOTE';
  /** A class id for a skin, `'*'` for an emote (any delver can wave). */
  appliesToClass: CharacterClassId | '*';
  /** EMOTE only — the clip inside `rig_<rig>_emotes.glb`. */
  clipName?: string;
  priceEmber: number;
  /** Usage, who it suits and who it does not. Optional for the same back-compat reason as `name`. */
  copy?: ItemCopy;
}

export interface CosmeticItemRow {
  id: string;
  cosmeticDefId: string;
  appliesToClass: CharacterClassId | '*';
  /**
   * Which delver is wearing it, or null for nobody (2026-09-07).
   *
   * User: *"1 skin is applieable for 1 character only."* This replaced an
   * `equipped` boolean, which was exclusive per *class* — two knights on one
   * account were forced to look identical, and one bought skin dressed both.
   */
  equippedCharacterId: string | null;
  /**
   * What the vendor pays to take it back, priced against what this player
   * actually paid rather than today's shelf price. Optional so a build that
   * predates the buyback counter still parses; a missing quote hides the
   * Sell button rather than guessing a number.
   */
  buybackEmber?: number;
}

export const getCosmeticCatalog = (): Promise<CosmeticDefRow[]> => api.get('/cosmetics/catalog');

export const getMyCosmetics = (): Promise<CosmeticItemRow[]> => api.get('/cosmetics/mine');

export const purchaseCosmetic = (cosmeticDefId: string): Promise<{ item: CosmeticItemRow; premiumBalance: number }> =>
  api.post('/cosmetics/purchase', { cosmeticDefId });

/** Both take a character since 2026-09-07 — a skin is worn by a delver, not by a class. */
export const equipCosmetic = (cosmeticDefId: string, characterId: string): Promise<CosmeticItemRow> =>
  api.post('/cosmetics/equip', { cosmeticDefId, characterId });

export const unequipCosmetic = (characterId: string): Promise<{ characterId: string }> =>
  api.post('/cosmetics/unequip', { characterId });

/**
 * Sell a skin or emote back.
 *
 * **Pays Ember, never Wax** — the daily-capped exchange is the only road
 * between the two currencies, and a buyback that crossed them would be a
 * second, uncapped one. Selling an equipped skin also takes it off, since
 * ownership is what equipping is a flag on.
 */
export const sellCosmetic = (cosmeticDefId: string): Promise<{
  cosmeticDefId: string; price: number; premiumBalance: number; wasEquipped: boolean;
}> => api.post('/cosmetics/sell', { cosmeticDefId });
